import type { FC } from 'react';
import React from 'react';
import CheckWrapper from './CheckWrapper';
import { createTheme, ThemeProvider } from '@mui/system';
import RedcatImage from './RedcatImage';
import { useAppSelector } from '../app/hooks';
import getThemeLookup from '../selectors/getThemeLookup';
import combineStyles from '../utils/combineStyles';
import { CONTAINER_PROPERTIES, TEXT_PROPERTIES } from '../utils/theme';
import { OrderingHooks } from 'polygon-ordering';
import Text from './Text';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';

const { useFormattedCurrency } = OrderingHooks;

interface ChoiceWithQuantity extends Choice {
  quantity?: number;
}
type Props = {
  // This is mainly used as a callback to change what is selected.
  onClick?: () => void;
  label?: JSX.Element;

  // This is only used not as input but as an indicator
  checked: boolean;

  size?: number;
  displayType?: string;
  choice?: ChoiceWithQuantity;
  disabled?: boolean;
  price?: number;
};

/***
 * TODO:
 * Bro what did they do to my simple component, it was only intended as a radio button 💀
 * make a new component called choiceSetCheck include that logic there
 */
const RadioCheck: FC<Props> = ({
  onClick,
  checked,
  label,
  size,
  displayType,
  choice,
  disabled = false,
  price = 0,
}) => {
  const p = useAppSelector(getThemeLookup);
  const isMobile = useAppSelector(getDeviceTypeMobile);

  const responsiveDefaultSize = isMobile ? 30 : 35;
  const sizeWithFallback = size || responsiveDefaultSize;

  const priceShowing = useFormattedCurrency({ cents: price });

  const radioCheckStyling = createTheme({
    palette: {
      borderRadius: '100%',
      size: sizeWithFallback,
    },
  });
  const iconVisible =
    displayType === 'list' || displayType === 'checkbox' || displayType === undefined;
  const displayGrid = displayType === 'grid';

  return (
    <ThemeProvider theme={radioCheckStyling}>
      <CheckWrapper onClick={onClick} disabled={disabled}>
        <div
          style={combineStyles(
            {
              display: 'flex',
              gap: 15,
              flexDirection: displayGrid ? 'column' : 'row',
              alignItems: 'center',
              cursor: 'pointer',
            },
            !displayGrid && { flex: 1 },
          )}
        >
          {iconVisible && <div className={`check-wrapper ${checked && 'checked'}`}></div>}
          {displayGrid && (
            <div style={{ width: 150, height: 100 }}>
              <RedcatImage
                alt={choice?.name || ''}
                imagePath={choice?.images?.default}
                containerStyle={styles.imageContainer}
                imageStyle={combineStyles(
                  styles.imageStyle,
                  { borderWidth: checked ? '2px' : '0' },
                  checked && p('purchaseEditorSelectedImage', ['border']),
                )}
              />
            </div>
          )}
          {Boolean(label) && (
            <div
              style={
                displayGrid
                  ? combineStyles(
                      {
                        padding: 10,
                        borderRadius: 10,
                        width: 150,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        flex: 1,
                      },
                      checked && p('itemChoiceActiveGrid', CONTAINER_PROPERTIES),
                    )
                  : { flex: 1 }
              }
            >
              {label}
              {displayGrid && (
                <div style={{ flex: 1 }}>
                  {price > 0 && (
                    <Text
                      style={combineStyles([
                        { textAlign: 'right', zIndex: -1 },
                        checked && p('itemChoiceActiveGrid', TEXT_PROPERTIES),
                      ])}
                    >{`+ ${priceShowing}`}</Text>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </CheckWrapper>
    </ThemeProvider>
  );
};

export default RadioCheck;

const styles: Styles = {
  imageContainer: {
    height: 100,
    minWidth: 150,
    maxWidth: 150,
  },
  imageStyle: {
    width: '100%',
    height: '100%',
    borderRadius: 10,
  },
};
